import {
  AmountInput,
  CheckboxField,
  DatePicker,
  FormField,
} from '@dev-spendesk/grapes';
import { useSelector } from 'react-redux';

import { AutocompleteMultipleSearch } from 'common/components/AutocompleteSearch';
import { useTranslation } from 'common/hooks/useTranslation';
import { useCompanyCurrency } from 'modules/app/hooks/useCompanyCurrency';
import { type CostCenter } from 'modules/budgets/models/costCenter';
import { type Team } from 'modules/budgets/models/team';
import { useFeature } from 'src/core/common/hooks/useFeature';
import FEATURES from 'src/core/constants/features';
import { SupplierFilterField } from 'src/core/modules/suppliers';
import {
  getIsFailedTransactionsEnabled,
  getIsMultiUseVirtualCardsEnabled,
} from 'src/core/selectors/globalSelectors';

import { type Filters } from './types';

type Props = {
  users: { id: string; fullname: string }[];
  teams: Team[];
  costCenters: CostCenter[];
  filters: Filters;
  setFilters: (filters: Filters) => void;
};

export const PaymentFiltersForm = ({
  users,
  teams,
  costCenters,
  filters,
  setFilters,
}: Props) => {
  const { t } = useTranslation('global');

  const currency = useCompanyCurrency();

  const isCostCentersFeatureEnabled = useFeature(
    FEATURES.COST_CENTERS_ACTIVATED,
  );

  const isFailedTransactionsEnabled = useSelector(
    getIsFailedTransactionsEnabled,
  );

  const isMultiUseCardsEnabled = useSelector(getIsMultiUseVirtualCardsEnabled);

  return (
    <>
      <FormField className="mb-24" label={t('forms.amount.label')}>
        <div className="flex flex-row items-center gap-8">
          <AmountInput
            fit="parent"
            hasNegativeValueAllowed
            placeholder={t('forms.numberRange.placeholder.min')}
            currency={{ key: currency, label: currency }}
            value={filters.minAmount ?? null}
            onChange={(_, newValue) => {
              if (Number.isNaN(newValue)) {
                const { minAmount, ...rest } = filters;
                setFilters(rest);
              } else {
                setFilters({ ...filters, minAmount: newValue });
              }
            }}
          />
          <AmountInput
            fit="parent"
            hasNegativeValueAllowed
            placeholder={t('forms.numberRange.placeholder.max')}
            currency={{ key: currency, label: currency }}
            value={filters.maxAmount ?? null}
            onChange={(_, newValue) => {
              if (Number.isNaN(newValue)) {
                const { maxAmount, ...rest } = filters;
                setFilters(rest);
              } else {
                setFilters({ ...filters, maxAmount: newValue });
              }
            }}
          />
        </div>
      </FormField>

      <FormField className="mb-24" label={t('requests.filters.period')}>
        <div className="flex flex-row items-center gap-8 [&>div]:w-full">
          <DatePicker
            fit="parent"
            placeholder={t('misc.from')}
            value={filters.fromPeriod}
            maxDate={filters.toPeriod}
            onChange={(date) => {
              if (!date) {
                const { fromPeriod, ...rest } = filters;
                setFilters(rest);
              } else {
                setFilters({ ...filters, fromPeriod: date });
              }
            }}
          />
          <DatePicker
            placement="bottom-end"
            fit="parent"
            placeholder={t('misc.to')}
            value={filters.toPeriod}
            minDate={filters.fromPeriod}
            onChange={(date) => {
              if (!date) {
                const { toPeriod, ...rest } = filters;
                setFilters(rest);
              } else {
                setFilters({ ...filters, toPeriod: date });
              }
            }}
          />
        </div>
      </FormField>

      <FormField className="mb-24" label={t('requests.filters.supplier')}>
        <SupplierFilterField
          placeholder={t('requests.filters.supplier')}
          value={filters.supplier ?? []}
          onChange={(supplierIds) => {
            setFilters({
              ...filters,
              supplier: supplierIds,
            });
          }}
        />
      </FormField>

      {users.length > 0 && (
        <FormField className="mb-24" label={t('requests.filters.requester')}>
          <AutocompleteMultipleSearch
            fit="parent"
            values={filters.requesters ?? []}
            options={users.map((user) => ({
              key: user.id,
              label: user.fullname,
            }))}
            translations={{
              selectAll: t('misc.selectAll'),
              selected: t('misc.nItemsSelected', {
                count: filters.requesters?.length,
              }),
            }}
            placeholder={t('requests.filters.requester')}
            onSelect={(selection) => {
              if (selection.length === 0) {
                const { requesters: requestersUnused, ...rest } = filters;
                setFilters(rest);
              } else {
                setFilters({ ...filters, requesters: selection });
              }
            }}
          />
        </FormField>
      )}

      <FormField className="mb-24" label={t('requests.filters.type')}>
        <AutocompleteMultipleSearch
          fit="parent"
          values={filters.types ?? []}
          options={[
            { key: 'expense', label: t('requests.filters.expense') },
            {
              key: 'single_purchase',
              label: t('requests.filters.single_purchase'),
            },
            {
              key: 'subscription',
              label: t('requests.filters.subscription'),
            },
            isMultiUseCardsEnabled && {
              key: 'multi_use_card',
              label: t('requests.filters.multi_use_card'),
            },
            { key: 'physical', label: t('forms.paymentType.plasticCard') },
            { key: 'invoice', label: t('requests.filters.invoice') },
            {
              key: 'mileage_allowance',
              label: t('requests.filters.mileageAllowance'),
            },
            {
              key: 'per_diem_allowance',
              label: t('requests.filters.per_diem'),
            },
          ].filter((o) => !!o)}
          translations={{
            selectAll: t('misc.selectAll'),
            selected: t('misc.nItemsSelected', {
              count: filters.types?.length,
            }),
          }}
          placeholder={t('requests.filters.type')}
          onSelect={(selectedTypes) => {
            if (selectedTypes.length === 0) {
              const { types, ...rest } = filters;
              setFilters(rest);
            } else {
              setFilters({ ...filters, types: selectedTypes });
            }
          }}
        />
      </FormField>

      <FormField className="mb-24" label={t('requests.filters.status')}>
        <AutocompleteMultipleSearch
          fit="parent"
          values={filters.statuses ?? []}
          options={[
            { key: 'authorised', label: t('forms.paymentStatus.pending') },
            { key: 'settled', label: t('forms.paymentStatus.paid') },
          ]}
          translations={{
            selectAll: t('misc.selectAll'),
            selected: t('misc.nItemsSelected', {
              count: filters.statuses?.length,
            }),
          }}
          placeholder={t('requests.filters.status')}
          onSelect={(selectedStatuses) => {
            if (selectedStatuses.length === 0) {
              const { statuses, ...rest } = filters;
              setFilters(rest);
            } else {
              setFilters({ ...filters, statuses: selectedStatuses });
            }
          }}
        />
      </FormField>

      <FormField className="mb-24" label={t('requests.filters.receipt')}>
        <AutocompleteMultipleSearch
          fit="parent"
          values={filters.documentaryEvidences ?? []}
          options={[
            { key: 'provided', label: t('forms.receipt.provided') },
            { key: 'missing', label: t('forms.receipt.missing') },
            { key: 'cannot_provide', label: t('forms.receipt.cannotProvide') },
          ]}
          translations={{
            selectAll: t('misc.selectAll'),
            selected: t('misc.nItemsSelected', {
              count: filters.documentaryEvidences?.length,
            }),
          }}
          placeholder={t('requests.filters.receipt')}
          onSelect={(selection) => {
            if (selection.length === 0) {
              const {
                documentaryEvidences: documentaryEvidencesUnused,
                ...rest
              } = filters;
              setFilters(rest);
            } else {
              setFilters({ ...filters, documentaryEvidences: selection });
            }
          }}
        />
      </FormField>

      {isCostCentersFeatureEnabled && (
        <FormField className="mb-24" label={t('requests.filters.costCenter')}>
          <AutocompleteMultipleSearch
            fit="parent"
            values={filters.costCenters ?? []}
            options={costCenters.map((costCenter) => ({
              key: costCenter.id,
              label: costCenter.name,
            }))}
            translations={{
              selectAll: t('misc.selectAll'),
              selected: t('misc.nItemsSelected', {
                count: filters.costCenters?.length,
              }),
            }}
            placeholder={t('requests.filters.costCenter')}
            onSelect={(selectedTypes) => {
              if (selectedTypes.length === 0) {
                const { costCenters: costCentersUnused, ...rest } = filters;
                setFilters(rest);
              } else {
                setFilters({ ...filters, costCenters: selectedTypes });
              }
            }}
          />
        </FormField>
      )}

      {teams.length > 0 && (
        <FormField className="mb-24" label={t('requests.filters.team')}>
          <AutocompleteMultipleSearch
            fit="parent"
            values={filters.teams ?? []}
            options={teams.map((team) => ({
              key: team.id,
              label: team.name,
            }))}
            translations={{
              selectAll: t('misc.selectAll'),
              selected: t('misc.nItemsSelected', {
                count: filters.teams?.length,
              }),
            }}
            placeholder={t('requests.filters.team')}
            onSelect={(selection) => {
              if (selection.length === 0) {
                const { teams: teamsUnused, ...rest } = filters;
                setFilters(rest);
              } else {
                setFilters({ ...filters, teams: selection });
              }
            }}
          />
        </FormField>
      )}
      {isFailedTransactionsEnabled && (
        <FormField
          className="mb-24"
          label={t('requests.filters.failedPayments')}
        >
          <CheckboxField
            label={t('misc.hidden')}
            isChecked={filters.hideFailedPayments === 'true'}
            onChange={(event) => {
              if (event.target.checked) {
                setFilters({ ...filters, hideFailedPayments: 'true' });
              } else {
                const { hideFailedPayments, ...rest } = filters;
                setFilters(rest);
              }
            }}
          />
        </FormField>
      )}
    </>
  );
};
